import blogOne from '#/assets/blog/blog-1.png'
import blogTwo from '#/assets/blog/blog-2.png'
import blogThree from '#/assets/blog/blog-3.png'

export type BlogSection = {
  heading: string
  body: (string | any[])[]
  quote?: { text: string; attribution?: string }
}

export type BlogPost = {
  id: string
  image: string
  title: string
  excerpt: string
  author: string
  authorBio: string
  authorInitials: string
  authorRole: string
  comments: string
  date: { day: string; month: string; year: string }
  tags: string[]
  readingTime: number
  category?: string
  sections: BlogSection[]
}

export const BLOG_POSTS: BlogPost[] = [
  {
    id: 'essence-of-tajweed',
    image: blogOne,
    title: 'The Essence Of Tajweed In Daily Recitation',
    excerpt:
      'Explore how tajweed refines pronunciation and deepens connection with the Quran through mindful recitation.',
    author: 'Ali Alaa Eldin',
    authorBio:
      'Ali is a certified Quran instructor with over 12 years of teaching experience. He holds an Ijazah in the recitation of Hafs from Asim and has taught students across 25+ countries.',
    authorInitials: 'AA',
    authorRole: 'Quran Instructor & Tajweed Specialist',
    comments: '8 Comments',
    date: { day: '09', month: 'Mar', year: '2024' },
    tags: ['Tajweed', 'Quran', 'Recitation'],
    readingTime: 6,
    category: 'Quran Studies',
    sections: [
      {
        heading: 'What Is Tajweed and Why Does It Matter?',
        body: [
          'Tajweed, derived from the Arabic root "jawwada" meaning to improve or make better, is the science of reciting the Quran with correct articulation, proper elongation, and precise pronunciation. Far from being a set of mechanical rules, tajweed is the art of breathing life into the words of Allah, honoring their sacred origin with the utmost precision.',
          'The Prophet Muhammad ﷺ received the Quran through Jibreel (AS) with specific intonation patterns, and those patterns have been preserved through an unbroken chain of oral transmission for over 1,400 years. Learning tajweed is therefore not just academic — it is an act of devotion and historical continuity.',
        ],
        quote: {
          text: 'And recite the Quran with measured recitation.',
          attribution: 'Quran 73:4',
        },
      },
      {
        heading: 'Core Principles Every Learner Should Know',
        body: [
          'The foundations of tajweed rest on four pillars: Makharij al-Huruf (the articulation points of letters), Sifat al-Huruf (the characteristics of letters), the rules of Noon Sakinah and Tanween, and the rules of Meem Sakinah. Each pillar supports the others, and weakness in one area will inevitably ripple into the rest of your recitation.',
          'Many beginners focus exclusively on memorisation, treating pronunciation as secondary. However, reciting a word incorrectly can change its meaning entirely — a concern that the scholars of tajweed took very seriously. The famous story of the man who recited "Ya Ayyuhan-Nasi" incorrectly in front of Imam Ali ibn Abi Talib illustrates precisely this point.',
        ],
      },
      {
        heading: 'Building a Daily Tajweed Practice',
        body: [
          'Consistency is the foundation of any skill, and tajweed is no different. Dedicate even fifteen minutes each morning to slow, deliberate recitation. Choose a short surah — perhaps Al-Mulk or Ya-Sin — and recite it one verse at a time, pausing to reflect on your pronunciation.',
          'Record yourself regularly. Hearing your own voice played back is one of the most powerful tools for self-correction. Compare your recitation with that of a reputable Qari such as Sheikh Mahmoud Khalil al-Husary or Sheikh Mishary Rashid Alafasy, paying close attention to the elongation of the Madd letters and the softness of the Ghunnah.',
          'The goal is not perfection in a single session but rather steady, gradual improvement. Scholars say that the Quran intercedes for those who recite it, and every sincere effort to improve your recitation is recorded as an act of worship.',
        ],
        quote: {
          text: 'The one who is proficient in the recitation of the Quran will be with the honourable and obedient scribes, and he who recites the Quran and finds it difficult to recite, doing his best to recite it in the best way possible, will have two rewards.',
          attribution: 'Sahih al-Bukhari & Muslim',
        },
      },
      {
        heading: 'Finding the Right Teacher',
        body: [
          'Tajweed is one of the few sciences that cannot be learned from a book alone. The mouth must hear and imitate. A qualified teacher — one who holds an Ijazah, a certified chain of transmission back to the Prophet ﷺ — is therefore essential.',
          "At Wa'allamah Institute, our instructors hold verified Ijazahs and bring years of teaching experience to every session. Whether you are a complete beginner or a student looking to refine advanced rules like Qalqalah and Tafkhim, we have a structured pathway designed for your level.",
        ],
      },
    ],
  },
  {
    id: 'learning-arabic-roots',
    image: blogTwo,
    title: 'Learning Arabic Roots For Deeper Understanding',
    excerpt:
      'A gentle guide to Arabic roots, helping learners uncover meaning across verses and daily vocabulary.',
    author: 'Huda Osman',
    authorBio:
      'Huda is an Arabic linguist and educator who has been teaching Quranic Arabic since 2010. Her unique method connects classical Arabic to modern understanding, making language accessible to every learner.',
    authorInitials: 'HO',
    authorRole: 'Arabic Language Educator',
    comments: '12 Comments',
    date: { day: '14', month: 'Mar', year: '2024' },
    tags: ['Arabic', 'Language', 'Study'],
    readingTime: 7,
    category: 'Arabic Language',
    sections: [
      {
        heading: "The Root System: Arabic's Greatest Gift",
        body: [
          'Arabic is built on a system of three-letter roots that generate vast families of related words. Understanding this root system is like being handed a master key that unlocks thousands of doors across the Quran, hadith, and classical literature.',
          'Take the root k-t-b (ك-ت-ب). From these three letters we get: kataba (he wrote), kitab (book), maktabah (library), katib (writer), maktub (written/letter), and istiktab (dictation). Every derivative carries the core meaning of "writing" in a different grammatical form.',
        ],
        quote: {
          text: 'And He taught Adam the names of all things.',
          attribution: 'Quran 2:31',
        },
      },
      {
        heading: 'A Practical Method for Root Learning',
        body: [
          'Start with the most frequent roots in the Quran. Research by scholars such as Dr. Abdur Rahim shows that the top 500 Arabic words cover roughly 75% of Quranic vocabulary. Prioritising these high-frequency roots will give you the fastest return on your study time.',
          'Use index cards or a flashcard app: write the root on one side, and a cluster of five derived words on the other. Review them during prayer preparation, commutes, or any short window of time. Spaced repetition — reviewing a card more frequently when it is new, then less as it becomes familiar — is scientifically proven to accelerate retention.',
        ],
      },
      {
        heading: 'Connecting Roots to Quranic Verses',
        body: [
          'The most effective way to cement a new root is to encounter it in context. After learning the root r-h-m (ر-ح-م — mercy), recite Surah Al-Fatiha with fresh eyes: ar-Rahman ar-Raheem — the Ever-Merciful, the Especially-Merciful. The same root appears 329 times across the Quran, each instance adding a new dimension to your understanding.',
          'Keep a vocabulary journal. When a root appears in your daily recitation and you recognise it, note the verse, the derived word, and the emotional resonance the meaning created. This journal becomes a personal, living dictionary unlike any textbook.',
        ],
      },
    ],
  },
  {
    id: 'ramadan-reflections',
    image: blogThree,
    title: 'Ramadan Reflections: Building Consistent Habits',
    excerpt:
      'Simple practices to build meaningful habits during Ramadan and keep them thriving beyond the month.',
    author: 'Omar Khalid',
    authorBio:
      'Omar is a life coach and Islamic studies instructor who specialises in habit formation through the lens of Islamic spirituality. He has guided thousands of students worldwide toward more intentional daily practices.',
    authorInitials: 'OK',
    authorRole: 'Islamic Life Coach & Instructor',
    comments: '6 Comments',
    date: { day: '22', month: 'Mar', year: '2024' },
    tags: ['Ramadan', 'Spirituality', 'Habits'],
    readingTime: 5,
    category: 'Spirituality',
    sections: [
      {
        heading: 'Why Ramadan Habits Rarely Survive',
        body: [
          'The spiritual energy of Ramadan is extraordinary. Mosques fill, family bonds deepen, and many Muslims experience their closest connection to Allah of the entire year. Yet by the second week of Shawwal, the Fajr prayers thin out and the long Quran recitations have returned to their pre-Ramadan length. Why does this happen — and what can we do differently?',
          'The answer lies in the difference between motivation and systems. Ramadan provides exceptional external motivation: community, atmosphere, collective intention. But motivation, as every behavioural scientist confirms, is inherently unstable. Systems — reliable, low-friction routines — are what outlast motivation.',
        ],
        quote: {
          text: 'The most beloved deeds to Allah are those that are most consistent, even if they are small.',
          attribution: 'Sahih al-Bukhari',
        },
      },
      {
        heading: 'The Three Habits Worth Anchoring',
        body: [
          "Instead of attempting to maintain all of Ramadan's intensity, identify three habits that felt genuinely nourishing rather than obligatory. Common candidates: a short morning Quran reading after Fajr, a midday dhikr set, and a du'a journal before sleeping. These three, maintained consistently, will do more for your spiritual development than attempting and failing to maintain twelve.",
          'Stack each habit onto an existing anchor in your daily routine. Quran after Fajr is natural because Fajr already exists as an anchor. Dhikr while your lunch heats up requires no extra time. The journal replaces five minutes of phone scrolling before sleep. When the habit is stackable, it requires less willpower to start.',
        ],
      },
      {
        heading: 'Making the Intention (Niyyah) Specific',
        body: [
          "Islam places remarkable emphasis on intention. The Prophet ﷺ said: 'Actions are but by intentions.' For habit formation, this principle maps precisely onto what modern psychology calls implementation intentions — the more specific your plan, the more likely you are to follow through.",
          "Don't say: 'I will recite Quran after Ramadan.' Say: 'Every day after Fajr salah, before I pick up my phone, I will recite five ayat of Surah Al-Baqarah.' The specificity removes the friction of decision-making in the moment, which is when most habits fail.",
        ],
      },
    ],
  },
  {
    id: 'quran-memorization-path',
    image: blogOne,
    title: 'A Clear Path To Quran Memorization',
    excerpt:
      'Break memorization into small, focused steps with checkpoints designed for lasting retention.',
    author: 'Maryam Noor',
    authorBio:
      'Maryam is a Hafizah and experienced Quran teacher who has helped over 300 students complete their memorization journey. She developed a proven revision system used by students across the globe.',
    authorInitials: 'MN',
    authorRole: 'Hafizah & Memorization Coach',
    comments: '9 Comments',
    date: { day: '02', month: 'Apr', year: '2024' },
    tags: ['Quran', 'Memorization', 'Study'],
    readingTime: 8,
    category: 'Quran Studies',
    sections: [
      {
        heading: 'Setting the Foundation Before You Begin',
        body: [
          "Memorizing the Quran is one of the most honourable pursuits a Muslim can undertake, but it requires a foundation built on sincerity, consistency, and realistic planning. Before committing a single verse to memory, clarify your intention. Are you memorizing for Allah's pleasure alone, or is there a mixture of social recognition involved? The purer the intention, the stronger the retention — not merely spiritually, but neurologically.",
          'Choose a single recitation to learn from and stick with it. Do not switch between Hafs, Warsh, and Qalun mid-memorization. The brain encodes sound patterns as part of the memory, and inconsistency in recitation will create interference and confusion.',
        ],
        quote: {
          text: 'The best of you is he who learns the Quran and teaches it.',
          attribution: 'Sahih al-Bukhari',
        },
      },
      {
        heading: 'The Daily Memorization Protocol',
        body: [
          'Memorize new verses in the morning only — specifically after Fajr, when the mind is fresh and the world is quiet. Neuroscience confirms that morning learning, followed by a day of normal activity and then sleep, produces stronger consolidation than evening learning followed immediately by sleep.',
          'For each new session: (1) Listen to your chosen Qari recite the new passage three times. (2) Read it aloud yourself, following the mushaf. (3) Close the mushaf and attempt to recite from memory. (4) Check and correct. (5) Repeat the correct version until it flows without hesitation. Never leave a session with an uncorrected mistake.',
        ],
      },
      {
        heading: 'The Revision System That Makes It Stick',
        body: [
          'New memorization is only the beginning. The real test is revision. A common approach that fails: memorize new verses daily, revise nothing, and find after a month that half of what was memorized has faded. The solution is a tiered revision system.',
          'Tier 1 — Daily: Revise the last 7 days of memorization. Tier 2 — Weekly: On Fridays, revise the last four weeks. Tier 3 — Monthly: On the first of each month, recite the entire completed portion of Quran in prayer. This three-tier system ensures that every verse is revisited at precisely the interval that prevents forgetting.',
        ],
      },
    ],
  },
  {
    id: 'family-halaqah',
    image: blogTwo,
    title: 'Building A Weekly Family Halaqah',
    excerpt:
      'Turn evenings into moments of learning with a simple, consistent halaqah routine for the home.',
    author: 'Yusuf Saleh',
    authorBio:
      'Yusuf is a community educator and father of four who has facilitated family learning circles for over a decade. He writes and speaks on the intersection of Islamic values and modern family life.',
    authorInitials: 'YS',
    authorRole: 'Community Educator',
    comments: '5 Comments',
    date: { day: '11', month: 'Apr', year: '2024' },
    tags: ['Family', 'Community', 'Habits'],
    readingTime: 5,
    category: 'Family & Community',
    sections: [
      {
        heading: 'Reclaiming the Evening for Knowledge',
        body: [
          'The Prophet ﷺ and his Companions would gather regularly to recite Quran, share knowledge, and remember Allah together. This tradition — the halaqah — predates every modern educational institution by over a millennium, and its simplicity is precisely what makes it so resilient.',
          'In many Muslim homes today, the evening hour belongs to screens. The halaqah does not compete with this by force but by beauty — by making the circle of knowledge so warm, so inviting, and so rewarding that children begin to look forward to it.',
        ],
      },
      {
        heading: 'Designing Your Family Halaqah',
        body: [
          'Consistency of time matters more than duration. A thirty-minute halaqah held every Sunday after Isha will produce far better results than a two-hour session attempted once a month. Choose a time that is already low-pressure: after a family meal, before the youngest children sleep, or immediately after Maghrib when everyone is gathered.',
          "Structure: Open with a short Quran recitation by each family member (even toddlers can say a short dua). Follow with a hadith — just one, brief and discussed together. End with a question: 'What is one thing I can do this week because of what we discussed?' Close with a collective du'a.",
        ],
        quote: {
          text: 'No people gather together in one of the houses of Allah, reciting the Book of Allah and studying it among themselves, except that tranquility descends upon them.',
          attribution: 'Sahih Muslim',
        },
      },
    ],
  },
  {
    id: 'sunnah-in-practice',
    image: blogThree,
    title: 'Sunnah In Practice: Small Deeds, Big Impact',
    excerpt:
      'Discover practical Sunnah habits that uplift daily life and bring calm to busy schedules.',
    author: 'Sara Tarek',
    authorBio:
      'Sara is an Islamic studies graduate and wellness advocate who focuses on the spiritual dimension of everyday routines. She writes extensively on mindfulness through the lens of the Sunnah.',
    authorInitials: 'ST',
    authorRole: 'Islamic Wellness Writer',
    comments: '7 Comments',
    date: { day: '19', month: 'Apr', year: '2024' },
    tags: ['Sunnah', 'Spirituality', 'Lifestyle'],
    readingTime: 5,
    category: 'Spirituality',
    sections: [
      {
        heading: 'The Power of the Small Deed',
        body: [
          "In an age that glorifies grand gestures and viral moments, the Sunnah consistently points in the opposite direction: toward the small, the consistent, and the sincere. 'The most beloved deeds to Allah are those that are most consistent, even if they are small,' the Prophet ﷺ said — and this principle cuts against every culture of excess and performance.",
          'The morning adhkar takes four minutes. Saying Bismillah before eating costs nothing. Using the right hand, beginning with the right side, smiling at a brother or sister — these are acts so small they barely register consciously. Yet their cumulative effect on the heart and on the household is transformative.',
        ],
        quote: {
          text: 'Do not belittle any act of goodness, even if it is just meeting your brother with a cheerful face.',
          attribution: 'Sahih Muslim',
        },
      },
      {
        heading: 'Five Sunnah Habits to Start This Week',
        body: [
          '1. Morning Adhkar — Before checking your phone, recite Ayat al-Kursi, the three Quls, and the morning supplications. The research on how a phone-free first fifteen minutes impacts the emotional tenor of your entire day is consistent and compelling.',
          '2. The Smile as Sadaqah — The Prophet ﷺ called smiling at your brother charity. Begin noticing your default facial expression when you encounter others, especially at home. The shift is low-effort and high-impact.',
          "3. Gratitude before Sleep — Before sleeping, recall three things from the day for which you are grateful to Allah specifically. Name them, not vaguely, but precisely. This practice directly counteracts the human mind's negativity bias.",
          '4. Two Rakaat of Duha — Even two light rakaat between sunrise and noon carry immense weight. The Messenger ﷺ said it was equivalent to giving charity on behalf of every joint in the body.',
          '5. Eating Together — The Prophet ﷺ said that food eaten together is blessed. In homes where the family eats separately — each with a screen — this single change, when implemented, often has ripple effects well beyond the meal itself.',
        ],
      },
    ],
  },
  {
    id: 'tajweed-mistakes',
    image: blogOne,
    title: 'Common Tajweed Mistakes And How To Fix Them',
    excerpt:
      'A helpful checklist of frequent tajweed errors and the simple adjustments that correct them.',
    author: 'Imran Farid',
    authorBio:
      'Imran is a Quran teacher with an Ijazah in the recitation of Hafs from Asim. He has been teaching tajweed online and in person since 2012 and is known for his clear, practical teaching style.',
    authorInitials: 'IF',
    authorRole: 'Certified Tajweed Instructor',
    comments: '11 Comments',
    date: { day: '03', month: 'May', year: '2024' },
    tags: ['Tajweed', 'Recitation', 'Practice'],
    readingTime: 7,
    category: 'Quran Studies',
    sections: [
      {
        heading: 'Mistake 1: Ignoring Madd (Elongation)',
        body: [
          "Madd, the elongation of certain vowel sounds, is one of the most commonly overlooked aspects of tajweed among beginners. When a student shortens a Madd Tabee'i (natural elongation) that should last two counts, or a Madd Muttasil that should last four to five counts, the rhythm and even the meaning of the recitation is disrupted.",
          'Fix: Use a metronome or a clapping rhythm when practising. Assign one beat per count of Madd. Natural elongation = 2 beats; Obligatory elongation = 4–5 beats. Drill this with short verses like the opening of Surah Al-Baqarah before moving on.',
        ],
      },
      {
        heading: 'Mistake 2: Incorrect Ghunnah',
        body: [
          'Ghunnah, the nasal resonance held at the back of the nose when pronouncing Meem and Noon in certain positions, is consistently either too short, too nasal, or placed incorrectly. Many students apply Ghunnah even when the rules of Ikhfa or Idgham do not call for it.',
          "Fix: Place your finger lightly under your nose and feel the vibration as you recite Noon Sakinah followed by a letter from the Ikhfa group. If you feel no vibration, your Ghunnah is absent. If you feel vibration on every Noon regardless of context, you're over-applying it. A teacher's ear is invaluable here.",
        ],
        quote: {
          text: 'Whoever reads a letter of the Book of Allah will have a reward, and that reward will be multiplied by ten.',
          attribution: 'Tirmidhi',
        },
      },
      {
        heading: 'Mistake 3: Merging Letters Without Distinction',
        body: [
          'Arabic has letter pairs that sound nearly identical to untrained ears: ص and س, ط and ت, ح and ه, ذ and ز and ظ. Confusing these letters is not merely a mispronunciation — it is a change of letter, and in some cases a change of meaning.',
          "Fix: Isolate the problematic pair. Spend a week doing nothing except drilling the two letters against each other: ص then س, back and forth, exaggerating the distinction until the mouth's muscle memory is established. Your teacher should listen and confirm that the distinction is audible to another person, not just to yourself.",
        ],
      },
    ],
  },
  {
    id: 'arabic-calligraphy',
    image: blogTwo,
    title: 'Introduction To Arabic Calligraphy',
    excerpt:
      'Explore the art of Arabic calligraphy and learn the tools and strokes used by master calligraphers.',
    author: 'Rania Selim',
    authorBio:
      'Rania is an award-winning calligrapher trained in Istanbul under Master Calligrapher Hasan Çelebi. She teaches traditional Arabic calligraphy styles including Naskh, Thuluth, and Diwani.',
    authorInitials: 'RS',
    authorRole: 'Master Calligrapher & Educator',
    comments: '4 Comments',
    date: { day: '12', month: 'May', year: '2024' },
    tags: ['Arabic', 'Art', 'Culture'],
    readingTime: 6,
    category: 'Arts & Culture',
    sections: [
      {
        heading: 'A Sacred Art Form',
        body: [
          'Arabic calligraphy is among the most sophisticated art forms humanity has produced. Unlike decorative scripts in other languages, Arabic calligraphy grew directly from the need to honour the words of Allah — to transcribe the Quran with beauty that matched the weight and majesty of its content.',
          "The first calligraphers were companions of the Prophet ﷺ, charged with the responsibility of recording revelation. This origin gives calligraphy a spiritual dimension that other art forms rarely carry. When a calligrapher writes 'Bismillah ar-Rahman ar-Raheem,' they are not merely writing — they are participating in a 1,400-year-old act of devotion.",
        ],
      },
      {
        heading: 'The Essential Scripts',
        body: [
          'Naskh is the most readable and widely used script — it forms the basis of modern printed Arabic text and the majority of printed Qurans. Its letters are clear, compact, and proportioned. For beginners, Naskh is the ideal starting point.',
          'Thuluth is the script of grandeur — characterized by elongated letters, sweeping curves, and dramatic compositions. You will find it inscribed above mosque entrances, on domes, and framing mihrab walls across the Islamic world. Mastering Thuluth typically takes three to five years of dedicated practice.',
          'Diwani was developed in the Ottoman courts and is characterized by densely interlocking letters and elaborate flourishes. It was historically used for official royal decrees and remains the most visually complex of the major scripts.',
        ],
        quote: {
          text: 'Beauty is in all things, but it is most beautiful in the written word.',
          attribution: 'Ali ibn Abi Talib (RA)',
        },
      },
    ],
  },
  {
    id: 'daily-dhikr',
    image: blogThree,
    title: 'Daily Dhikr Routine For Busy Learners',
    excerpt:
      'Short, meaningful dhikr sets that fit into a packed day and keep hearts aligned.',
    author: 'Bilal Hassan',
    authorBio:
      'Bilal is a student of Islamic sciences and productivity coach who has built a following of over 50,000 readers with his practical Islamic lifestyle content. He believes deeply in the integration of faith and modern life.',
    authorInitials: 'BH',
    authorRole: 'Islamic Productivity Coach',
    comments: '10 Comments',
    date: { day: '21', month: 'May', year: '2024' },
    tags: ['Dhikr', 'Spirituality', 'Habits'],
    readingTime: 5,
    category: 'Spirituality',
    sections: [
      {
        heading: 'Why the Tongue Should Never Be Still',
        body: [
          'Allah describes dhikr as the nourishment of the heart: "Verily, in the remembrance of Allah do hearts find rest." (13:28). This is not poetic metaphor — it is a prescription for the fundamental human need that every modern therapy attempts to address through other means: the need for groundedness, presence, and transcendence.',
          'The challenge for busy people is not motivation — most Muslims want to engage in dhikr. The challenge is integration: weaving remembrance into the fabric of a full, demanding day rather than treating it as an additional task competing for scheduled time.',
        ],
        quote: {
          text: 'Verily, in the remembrance of Allah do hearts find rest.',
          attribution: 'Quran 13:28',
        },
      },
      {
        heading: 'The Micro-Dhikr Approach',
        body: [
          'Micro-dhikr means associating brief remembrance with transitions that already exist in your day. Every time you walk through a door: SubhanAllah. Every time you sit down at your desk: Bismillah. Every time you feel your phone vibrate: Astaghfirullah. These associations require no extra time — they run alongside activities rather than competing with them.',
          'The key is to choose one association per week and repeat it until it becomes automatic before adding another. Trying to build ten new dhikr associations simultaneously is as effective as learning ten new languages simultaneously — which is to say, not very.',
        ],
      },
      {
        heading: 'The Power of Tasbih (Counting on Fingers)',
        body: [
          'The Prophet ﷺ instructed his Companions to use their fingers for tasbeeh, saying that the fingers will be questioned on the Day of Judgment and will testify. This is not incidental. The physical act of counting on fingers — rather than relying on electronic counters — creates a tactile, embodied experience of remembrance that deepens presence.',
          'Commit to 33 SubhanAllah, 33 Alhamdulillah, and 34 Allahu Akbar after each of the five daily prayers. This takes under two minutes and was the specific practice taught by the Prophet ﷺ as a replacement for servants for those who could not afford them — describing it as better than everything the world contains.',
        ],
      },
    ],
  },
]

export const BLOG_TAGS = Array.from(new Set(BLOG_POSTS.flatMap((p) => p.tags)))

export function getBlogById(id: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.id === id)
}

export function getRelatedPosts(current: BlogPost, limit = 3): BlogPost[] {
  return BLOG_POSTS.filter(
    (p) => p.id !== current.id && p.tags.some((t) => current.tags.includes(t)),
  ).slice(0, limit)
}
